import { SuggestionOptions, SuggestionProps } from "@tiptap/suggestion";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { MentionSuggestion } from "./suggestion-options";
import { cn } from "@/lib/utils";
import ProfilePicture from "../../profile-picture";

export type MentionListRef = {
	onKeyDown: NonNullable<
		ReturnType<NonNullable<SuggestionOptions<MentionSuggestion>["render"]>>["onKeyDown"]
	>;
};

export type MentionListProps = SuggestionProps<MentionSuggestion>;

export interface MentionNodeAttrs {
	id: string | null;
	label?: string | null;
	image?: string | null;
}

const MentionList = forwardRef<MentionListRef, MentionListProps>((props, ref) => {
	const [selectedIndex, setSelectedIndex] = useState<number>(0);

	const selectItem = (index: number) => {
		if (index >= props.items.length) {
			return;
		}

		const suggestion = props.items[index];

		const mentionItem: MentionNodeAttrs = {
			id: suggestion.id,
			label: suggestion.label,
		};

		// @ts-expect-error there is currently a bug in the Tiptap SuggestionProps
		// type where if you specify the suggestion type (like
		// `SuggestionProps<MentionSuggestion>`), it will incorrectly require that
		// type variable for `command`'s argument as well (whereas instead the
		// type of that argument should be the Mention Node attributes). This
		// should be fixed once https://github.com/ueberdosis/tiptap/pull/4136 is
		// merged and we can add a separate type arg to `SuggestionProps` to
		// specify the type of the commanded selected item.
		props.command(mentionItem);
	};

	const upHandler = () => {
		setSelectedIndex((selectedIndex + props.items.length - 1) % props.items.length);
	};

	const downHandler = () => {
		setSelectedIndex((selectedIndex + 1) % props.items.length);
	};

	const enterHandler = () => {
		selectItem(selectedIndex);
	};

	useEffect(() => setSelectedIndex(0), [props.items]);

	useImperativeHandle(ref, () => ({
		onKeyDown: ({ event }) => {
			if (event.key === "ArrowUp") {
				upHandler();
				return true;
			}

			if (event.key === "ArrowDown") {
				downHandler();
				return true;
			}

			if (event.key === "Enter") {
				enterHandler();
				return true;
			}

			return false;
		},
	}));

	return (
		<div className="grid w-64 gap-0.5 rounded-lg bg-white shadow">
			{props.items.length ? (
				props.items.map((item, index) => (
					<button
						key={index}
						className={cn(
							"flex items-center justify-start gap-1 px-3 py-3 text-left text-sm text-gray-600 hover:bg-gray-100",
							index === selectedIndex ? "bg-gray-100 text-brand-primary" : "bg-transparent"
						)}
						onClick={() => selectItem(index)}
					>
						<div>
							<ProfilePicture
								profilePictureUrl={item.image}
								alternateText={item.label}
								className={{ container: "h-5 w-5", image: "rounded-full object-contain" }}
							/>
						</div>
						<span>{item.label}</span>
					</button>
				))
			) : (
				<div className="block px-4 py-3 text-left text-sm text-gray-500">No results found</div>
			)}
		</div>
	);
});

MentionList.displayName = "MentionList";

export default MentionList;
